import { forwardRef } from 'react';

import { Table, TableBody } from '@/common/components/ui/table';
import { AssignmentTableHeader } from '@/policy/component/Assignment/AssignmentTableHeader';
import { AssignmentTableRows } from '@/policy/component/Assignment/AssignmentTableRows';
import { ScrollShadows } from '@/policy/component/ScrollShadows/ScrollShadows';
import { ValidableSection } from '@/policy/component/Validation/ValidableSection';
import { useScroll } from '@/policy/hooks/useScroll';
import { AssignmentSection } from '@/policy/types/PolicyCoverage';
import { ValidationResult } from '@/policy/types/ValidationResult';

export type AssignmentSectionProps = {
  section: AssignmentSection;
  validation: ValidationResult;
  onValidationChange: (validation: ValidationResult) => void;
  isSelected: boolean;
  onTableClick: (sectionUuid: string) => void;
};

export const AssignmentsSection = forwardRef<
  HTMLDivElement,
  AssignmentSectionProps
>(
  (
    { section, validation, onValidationChange, isSelected, onTableClick },
    ref,
  ) => {
    const { scrollContainerRef, dragProps, canScrollLeft, canScrollRight } =
      useScroll();

    return (
      <ValidableSection
        ref={ref}
        sectionIdentifier={section.identifier}
        validation={validation}
        onValidationChange={onValidationChange}
        isSelected={isSelected}
        onTableClick={onTableClick}
      >
        <ScrollShadows
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
        >
          <div
            ref={scrollContainerRef}
            {...dragProps}
            className="whitespace-pre-line text-xxs w-full overflow-x-auto scrollbar-visible
            [&::-webkit-scrollbar]:h-1.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
            [&::-webkit-scrollbar-thumb]:rounded-full"
          >
            <Table>
              <AssignmentTableHeader headers={section.headers} />
              <TableBody>
                <AssignmentTableRows
                  headers={section.headers}
                  assignmentRowData={section.rows}
                />
              </TableBody>
            </Table>
          </div>
        </ScrollShadows>
      </ValidableSection>
    );
  },
);
